/**
 * Service layer for the Meta delivery workspace — thin typed wrappers over
 * `integrationsClient` for the connection-scoped surface of the integration-
 * service UI contract: feed generation, the staged-feed inbox/review reads, the
 * per-product audit timeline, connector capabilities, coverage (snapshot, history,
 * value-check product drill-down and insights) and events.
 *
 * Casing: camelCase both ways EXCEPT `supplemental-feed:generate`, whose request
 * and response are snake_case — that one call has its own wire type and an
 * explicit mapper right here, so the exception can't leak into UI state.
 *
 * Several stage-8 reads (coverage, events) and the stage-3 inbox/headers/filters
 * reads are specified by purpose rather than shape in the contract, so their
 * payloads are normalised defensively (`unwrapList`, `toHeaders`, …): an
 * unexpected wrapper degrades to an empty list, never a crash. Tenant scope is
 * the JWT `subsidiaryId` claim injected by the client interceptors.
 */
import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { COVERAGE_SEARCH_MAX_LENGTH } from '~/features/coverage-report/types/coverage-v3.types';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';

import type {
  CoverageRedeliverJob,
  CoverageRedeliverRun,
  CoverageRedeliverSkip,
  CoverageRedeliverStart,
} from '../types/coverage-redeliver.types';
import type { DeliveryPagination, JsonMap } from '../types/delivery-runs.types';
import type {
  ConnectionCapabilities,
  ConnectionEvent,
  ConnectionEventsFilters,
  ConnectionEventsPage,
  ConnectionTestRequest,
  ConnectionTestResult,
  CoverageDeliveredVia,
  CoverageHistoryPoint,
  CoverageHistoryQuery,
  CoverageInsight,
  CoverageInsightAction,
  CoverageInsightRoute,
  CoverageProductRow,
  CoverageProductsPage,
  CoverageProductsQuery,
  CoverageSnapshot,
  GenerateSupplementalFeedRequest,
  GeneratedSupplementalFeed,
  GeneratedSupplementalFeedWire,
  ProductTimeline,
  ProductTimelineQuery,
  StagedUpload,
  StagedUploadsFilters,
  StagedUploadsPage,
  UploadFilterField,
  UploadHeader,
  UploadQualitySummary,
} from '../types/meta-delivery.types';

// ---------------------------------------------------------------------------
// Normalisation helpers
// ---------------------------------------------------------------------------

/** Narrow an unknown JSON value to a plain object. */
function isRecord(value: unknown): value is JsonMap {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read a string field; `null` when absent or not a string. */
function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** Read a numeric field (accepting numeric strings); `null` otherwise. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/** First present key among `keys` on `source`. */
function pick(source: JsonMap, keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined) return source[key];
  }
  return undefined;
}

/**
 * Accept either the console's 8-field pagination block or the contract's
 * 5-field `{ total, page, size, pages, hasNext }` and project both onto
 * `DeliveryPagination`. `null` when the payload carried neither.
 */
function toPagination(value: unknown): DeliveryPagination | null {
  if (!isRecord(value)) return null;
  const total = asNumber(value.total);
  const page = asNumber(value.page);
  const size = asNumber(value.size);
  if (total === null || page === null || size === null) return null;
  const totalPages =
    asNumber(pick(value, ['totalPages', 'pages'])) ??
    Math.max(1, Math.ceil(total / Math.max(1, size)));
  const hasNextPage =
    typeof value.hasNextPage === 'boolean'
      ? value.hasNextPage
      : typeof value.hasNext === 'boolean'
        ? value.hasNext
        : page < totalPages;
  const hasPrevPage = typeof value.hasPrevPage === 'boolean' ? value.hasPrevPage : page > 1;
  return {
    total,
    page,
    size,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage: hasNextPage ? page + 1 : null,
    prevPage: hasPrevPage ? page - 1 : null,
  };
}

/**
 * Pull the row array + pagination out of a list response whose exact wrapper is
 * not pinned by the contract: `data` may be the array itself or an object holding
 * it under one of `keys`; `pagination` may sit beside `data` or inside it.
 */
function unwrapList(
  body: unknown,
  keys: string[],
): { items: JsonMap[]; pagination: DeliveryPagination | null } {
  if (!isRecord(body)) return { items: [], pagination: null };
  const data = body.data;
  if (Array.isArray(data)) {
    return { items: data.filter(isRecord), pagination: toPagination(body.pagination) };
  }
  if (isRecord(data)) {
    const inner = pick(data, keys);
    const items = Array.isArray(inner) ? inner.filter(isRecord) : [];
    return { items, pagination: toPagination(data.pagination) ?? toPagination(body.pagination) };
  }
  return { items: [], pagination: null };
}

/** Primitive query-param values Ky accepts on `searchParams`. */
type ParamValue = string | number | boolean;

/** Build a Ky `searchParams` record, dropping null/undefined entries. */
function toSearchParams(params: Record<string, ParamValue | null | undefined>) {
  const out: Record<string, ParamValue> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Stage 1 · capabilities + credential test
// ---------------------------------------------------------------------------

/** Which resources/actions the connector behind this connection supports. */
export async function getConnectionCapabilities(
  connectionId: string,
): Promise<ConnectionCapabilities> {
  const res = await integrationsClient
    .get(endpoints.connections.capabilities(connectionId))
    .json<ApiResponse<unknown>>();
  return isRecord(res.data) ? res.data : {};
}

/** Dry-run connector credentials before creating a connection row. */
export async function testConnection(body: ConnectionTestRequest): Promise<ConnectionTestResult> {
  const res = await integrationsClient
    .post(endpoints.connections.test(), { json: body })
    .json<ApiResponse<ConnectionTestResult>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Stage 2 · generate the supplemental feed (snake_case both ways)
// ---------------------------------------------------------------------------

/**
 * Turn enrichment output into a Meta-shaped TSV staged for review. Maps the
 * camelCase request onto the snake_case body and the snake_case response back —
 * the single casing exception on this surface. Errors: 400 connection not
 * configured for supplemental feed / feed id missing; 404 no enrichment rows
 * matched; 422 rows matched but none carried override values; 503 store down.
 */
export async function generateSupplementalFeed(
  connectionId: string,
  request: GenerateSupplementalFeedRequest = {},
): Promise<GeneratedSupplementalFeed> {
  const body: Record<string, unknown> = {};
  if (request.workflowId) body.workflow_id = request.workflowId;
  if (request.externalIds && request.externalIds.length > 0)
    body.external_ids = request.externalIds;
  if (request.limit != null && request.limit > 0) body.limit = request.limit;

  const res = await integrationsClient
    .post(endpoints.connections.supplementalFeedGenerate(connectionId), { json: body })
    .json<ApiResponse<GeneratedSupplementalFeedWire>>();
  const wire = res.data;
  return {
    uploadId: wire.upload_id,
    workflowId: wire.workflow_id,
    rowCount: wire.row_count,
    gcsBlobName: wire.gcs_blob_name,
    status: wire.status,
    generationRunId: wire.generation_run_id ?? null,
  };
}

// ---------------------------------------------------------------------------
// Stage 3 · staged-feed inbox + review reads
// ---------------------------------------------------------------------------

/** Project an upload row (whatever subset of fields it carries) onto `StagedUpload`. */
function toStagedUpload(raw: JsonMap): StagedUpload | null {
  const id = asString(pick(raw, ['id', 'uploadId', 'upload_id']));
  if (!id) return null;
  return {
    id,
    status: asString(raw.status) ?? '',
    filename: asString(pick(raw, ['filename', 'fileName'])),
    connectionId: asString(pick(raw, ['connectionId', 'connection_id'])),
    uploadGroupId: asString(pick(raw, ['uploadGroupId', 'upload_group_id'])),
    rowCount: asNumber(pick(raw, ['rowCount', 'rowsIngested', 'row_count', 'totalRows'])),
    createdAt: asString(pick(raw, ['createdAt', 'created_at'])),
    updatedAt: asString(pick(raw, ['updatedAt', 'updated_at'])),
    workflowId: asString(pick(raw, ['workflowId', 'workflow_id'])),
    errorReason: asString(pick(raw, ['errorReason', 'error_reason'])),
  };
}

/** The staged-feed inbox — `POST /uploads/list`, filtered to a connection and/or status. */
export async function listStagedUploads(
  filters: StagedUploadsFilters,
  page = 1,
  size = 20,
): Promise<StagedUploadsPage> {
  const body = {
    filters: {
      ...(filters.connectionId ? { connectionId: filters.connectionId } : {}),
      ...(filters.status ? { status: filters.status } : {}),
    },
    pagination: { page, size },
  };
  const res = await integrationsClient
    .post(endpoints.uploads.list(), { json: body })
    .json<unknown>();
  const { items, pagination } = unwrapList(res, ['uploads', 'items', 'results']);
  return {
    items: items.map(toStagedUpload).filter((u): u is StagedUpload => u !== null),
    pagination,
  };
}

/**
 * Column names + inferred datatypes. Accepts the shapes the endpoint could
 * plausibly send — a bare string array, `{ name, type }` objects, a
 * `{ headers | columns: [...] }` wrapper, or a name → type map — and projects
 * all of them onto `UploadHeader[]`.
 */
function toHeaders(data: unknown): UploadHeader[] {
  const list: unknown =
    isRecord(data) && !Array.isArray(data) ? pick(data, ['headers', 'columns']) : data;
  if (Array.isArray(list)) {
    return list.flatMap((entry): UploadHeader[] => {
      if (typeof entry === 'string') return [{ name: entry, dataType: null }];
      if (!isRecord(entry)) return [];
      const name = asString(pick(entry, ['name', 'column', 'key', 'header']));
      if (!name) return [];
      const dataType = asString(
        pick(entry, ['dataType', 'data_type', 'type', 'inferredType', 'inferred_type']),
      );
      return [{ name, dataType }];
    });
  }
  if (isRecord(data)) {
    return Object.entries(data).flatMap(([name, type]) =>
      typeof type === 'string' ? [{ name, dataType: type }] : [],
    );
  }
  return [];
}

/** Column names + inferred datatypes of a staged feed (`GET /uploads/{id}/headers`). */
export async function getUploadHeaders(uploadId: string): Promise<UploadHeader[]> {
  const res = await integrationsClient
    .get(endpoints.uploads.headers(uploadId))
    .json<ApiResponse<unknown>>();
  return toHeaders(res.data);
}

/** Which fields of a staged feed are filterable (`GET /uploads/{id}/filters`). */
export async function getUploadFilterFields(uploadId: string): Promise<UploadFilterField[]> {
  const res = await integrationsClient
    .get(endpoints.uploads.filters(uploadId))
    .json<ApiResponse<unknown>>();
  const data = res.data;
  const list: unknown = isRecord(data) ? pick(data, ['filters', 'fields', 'items']) : data;
  if (!Array.isArray(list)) return [];
  return list.flatMap((entry): UploadFilterField[] => {
    if (typeof entry === 'string') return [{ key: entry, label: entry, type: null }];
    if (!isRecord(entry)) return [];
    const key = asString(pick(entry, ['key', 'field', 'column', 'name']));
    if (!key) return [];
    return [{ key, label: asString(entry.label) ?? key, type: asString(entry.type) }];
  });
}

/**
 * Quality KPIs for a staged feed. GET when no filters are applied; the POST
 * variant is filter-aware and takes the same `appliedFilters` dict the approve
 * call commits.
 */
export async function getUploadQualitySummary(
  uploadId: string,
  appliedFilters: JsonMap | null = null,
): Promise<UploadQualitySummary> {
  const req = appliedFilters
    ? integrationsClient.post(endpoints.uploads.summary(uploadId), { json: { appliedFilters } })
    : integrationsClient.get(endpoints.uploads.summary(uploadId));
  const res = await req.json<ApiResponse<unknown>>();
  return isRecord(res.data) ? res.data : {};
}

/** Cancel a staged feed and delete its blob (`DELETE /uploads/{id}`). Reversible
 *  stage — nothing has been sent yet. */
export async function deleteStagedUpload(uploadId: string): Promise<void> {
  await integrationsClient.delete(endpoints.uploads.cancel(uploadId));
}

// ---------------------------------------------------------------------------
// Stage 7 · per-product audit timeline
// ---------------------------------------------------------------------------

/**
 * Every delivery job one product appeared in on this connection, newest first.
 * 404 = no history at all (the message names the idType used); 503 = the
 * history store is unavailable (retry, don't show an empty state); 422 = a bad
 * `idType` / `limit`. Callers branch on `ApiError.statusCode`.
 */
export async function getProductTimeline(
  connectionId: string,
  productId: string,
  query: ProductTimelineQuery = {},
): Promise<ProductTimeline> {
  const res = await integrationsClient
    .get(endpoints.deliveries.productTimeline(connectionId, productId), {
      searchParams: toSearchParams({
        idType: query.idType,
        limit: query.limit,
        offset: query.offset,
        includePayload: query.includePayload,
      }),
    })
    .json<ApiResponse<ProductTimeline>>();
  const data = res.data;
  return {
    ...data,
    total: asNumber(data.total) ?? data.entries.length,
    truncated: data.truncated,
    entries: Array.isArray(data.entries) ? data.entries : [],
  };
}

// ---------------------------------------------------------------------------
// Stage 8 · coverage + audit trail
// ---------------------------------------------------------------------------

/** Latest coverage snapshot for a connection. */
export async function getConnectionCoverage(connectionId: string): Promise<CoverageSnapshot> {
  const res = await integrationsClient
    .get(endpoints.connections.coverage(connectionId))
    .json<ApiResponse<unknown>>();
  return isRecord(res.data) ? res.data : {};
}

/** Coverage over time (trend) between `fromDate` and `toDate`, inclusive. */
export async function getConnectionCoverageHistory(
  connectionId: string,
  query: CoverageHistoryQuery = {},
): Promise<CoverageHistoryPoint[]> {
  const res = await integrationsClient
    .get(endpoints.connections.coverageHistory(connectionId), {
      searchParams: toSearchParams({ fromDate: query.fromDate, toDate: query.toDate }),
    })
    .json<unknown>();
  return unwrapList(res, ['history', 'points', 'items', 'snapshots']).items;
}

/**
 * Project one drill-down row onto `CoverageProductRow`. Every coverage-v3 field is optional on
 * the wire (a row written before the value check carries none), so absent values become
 * empty strings / `null` here rather than `undefined` in the table.
 */
function toCoverageProductRow(raw: JsonMap): CoverageProductRow {
  const offerId = asString(raw.offerId);
  return {
    externalId: asString(raw.externalId) ?? offerId ?? '',
    offerId,
    pdState: asString(raw.pdState) ?? '',
    gapReason: asString(raw.gapReason) ?? '',
    attrsExpected: asNumber(raw.attrsExpected),
    attrsLive: asNumber(raw.attrsLive),
    sdState: asString(raw.sdState) ?? '',
    judgedOn: (asString(raw.judgedOn) ?? '').toLowerCase(),
    deliveredAt: asString(raw.deliveredAt),
    deliveryJobId: asString(raw.deliveryJobId),
    verdictVersion: asNumber(raw.verdictVersion),
    expectedFrom: asString(raw.expectedFrom),
    gapDetail: asString(raw.gapDetail),
    gapRoute: asString(raw.gapRoute),
    expectedEnrichedAt: asString(raw.expectedEnrichedAt),
    deliveredVia: Array.isArray(raw.deliveredVia)
      ? raw.deliveredVia.flatMap(toCoverageDeliveredVia)
      : [],
  };
}

/** Project one `deliveredVia[]` entry; one without a connection id or method is dropped. */
function toCoverageDeliveredVia(raw: unknown): CoverageDeliveredVia[] {
  if (!isRecord(raw)) return [];
  const connectionId = asString(raw.connectionId);
  const method = asString(raw.method);
  if (connectionId === null || method === null) return [];
  return [
    {
      connectionId,
      connectorCode: asString(raw.connectorCode),
      method,
      linked: raw.linked === true,
      deliveredAt: asString(raw.deliveredAt),
      recordedContent: raw.recordedContent !== false,
      matchesExpected: raw.matchesExpected === true,
    },
  ];
}

/** Project one insight route (contract 14.24); one without a connection id or method is dropped. */
function toCoverageInsightRoute(raw: unknown): CoverageInsightRoute[] {
  if (!isRecord(raw)) return [];
  const connectionId = asString(raw.connectionId);
  const method = asString(raw.method);
  if (connectionId === null || method === null) return [];
  return [
    { connectionId, method, linked: raw.linked === true, products: asNumber(raw.products) ?? 0 },
  ];
}

/**
 * Per-product coverage drill-down page (coverage v3). Filters by `state` / `reason` /
 * `field` / `search`; the server reads only the latest finished run. `search` is trimmed and cut
 * to the contract's 200 characters (a longer one is a 422); a blank one is not sent, so an emptied
 * box never narrows the list.
 */
export async function getConnectionCoverageProducts(
  connectionId: string,
  query: CoverageProductsQuery = {},
): Promise<CoverageProductsPage> {
  const search = query.search?.trim().slice(0, COVERAGE_SEARCH_MAX_LENGTH).trim();
  const res = await integrationsClient
    .get(endpoints.connections.coverageProducts(connectionId), {
      searchParams: toSearchParams({
        page: query.page,
        size: query.size,
        state: query.state,
        reason: query.reason,
        field: query.field,
        search: search === '' ? undefined : search,
        route: query.route,
        detail: query.detail,
        gapRoute: query.gapRoute,
      }),
    })
    .json<unknown>();
  const { items, pagination } = unwrapList(res, ['products', 'items', 'results']);
  return { items: items.map(toCoverageProductRow), pagination };
}

/** Project an insight's `action` (`null` when absent or not an object). */
function toCoverageInsightAction(raw: unknown): CoverageInsightAction | null {
  if (!isRecord(raw)) return null;
  const type = asString(raw.type);
  const label = asString(raw.label);
  if (type === null && label === null) return null;
  return {
    type: type ?? '',
    label: label ?? '',
    method: asString(raw.method),
    path: asString(raw.path),
    body: isRecord(raw.body) ? raw.body : null,
  };
}

/** Project one insight; a record without a `code` is not an insight and is dropped. */
function toCoverageInsight(raw: JsonMap): CoverageInsight | null {
  const code = asString(raw.code);
  if (code === null) return null;
  const severity = asString(raw.severity)?.toLowerCase();
  return {
    code,
    severity: severity === 'high' || severity === 'medium' ? severity : 'low',
    count: asNumber(raw.count) ?? 0,
    impactPts: asNumber(raw.impactPts) ?? 0,
    title: asString(raw.title) ?? '',
    detail: asString(raw.detail) ?? '',
    action: toCoverageInsightAction(raw.action),
    routes: Array.isArray(raw.routes) ? raw.routes.flatMap(toCoverageInsightRoute) : [],
  };
}

/**
 * Coverage insights for a connection — computed by the server at read time from the latest
 * `coverage_daily` row ("what to do next"). 404 = no coverage row yet.
 */
export async function getConnectionCoverageInsights(
  connectionId: string,
): Promise<CoverageInsight[]> {
  const res = await integrationsClient
    .get(endpoints.connections.coverageInsights(connectionId))
    .json<unknown>();
  return unwrapList(res, ['insights', 'items', 'results'])
    .items.map(toCoverageInsight)
    .filter((insight): insight is CoverageInsight => insight !== null);
}

/**
 * Recompute coverage on demand. Returns whatever handle the server reports. `force` bypasses the
 * workflow's same-day full-pull skip (a plain trigger after today's full read changes nothing) and
 * replaces a same-day run still in flight; it is sent only when set.
 */
export async function triggerConnectionCoverage(
  connectionId: string,
  { force = false }: { force?: boolean } = {},
): Promise<JsonMap> {
  const res = await integrationsClient
    .post(
      endpoints.connections.coverageTrigger(connectionId),
      force ? { searchParams: { force: 'true' } } : {},
    )
    .json<ApiResponse<unknown>>();
  return isRecord(res.data) ? res.data : {};
}

/**
 * Project one delivery attempt of a redeliver job. One that started no delivery (`pipelineRunId`
 * null) is kept: the job's failed / total counts include it, and its `message` says why.
 */
function toCoverageRedeliverRun(raw: JsonMap): CoverageRedeliverRun {
  return {
    pipelineRunId: asString(raw.pipelineRunId),
    sourceRunId: asString(raw.sourceRunId),
    enrichmentWorkflowId: asString(raw.enrichmentWorkflowId),
    chunkIndex: asNumber(raw.chunkIndex) ?? 0,
    attempt: asNumber(raw.attempt) ?? 0,
    products: asNumber(raw.products) ?? 0,
    status: asString(raw.status) ?? '',
    startedAt: asString(raw.startedAt),
    finishedAt: asString(raw.finishedAt),
    message: asString(raw.message),
  };
}

/** Project one skip entry; one without a reason or with no product is not shown. */
function toCoverageRedeliverSkip(raw: JsonMap): CoverageRedeliverSkip | null {
  const reason = asString(raw.reason);
  const products = asNumber(raw.products) ?? 0;
  if (!reason || products <= 0) return null;
  return { reason, products, groups: asNumber(raw.groups) ?? 0 };
}

/** Reason code → count, keeping only numeric entries. */
function toCountMap(raw: unknown): Record<string, number> {
  if (!isRecord(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw).flatMap(([code, value]) => {
      const count = asNumber(value);
      return count === null ? [] : [[code, count] as const];
    }),
  );
}

/** Project a redeliver job's status (`data` of the GET); every count defaults to 0. */
function toCoverageRedeliverJob(raw: JsonMap): CoverageRedeliverJob {
  const count = (value: unknown) => asNumber(value) ?? 0;
  const list = (value: unknown) => (Array.isArray(value) ? value.filter(isRecord) : []);
  return {
    jobId: asString(raw.jobId) ?? '',
    connectionId: asString(raw.connectionId) ?? '',
    snapshotDate: asString(raw.snapshotDate) ?? '',
    trigger: asString(raw.trigger) ?? 'USER',
    state: asString(raw.state) ?? '',
    stateReason: asString(raw.stateReason),
    startedAt: asString(raw.startedAt),
    finishedAt: asString(raw.finishedAt),
    waitingForSlotSince: asString(raw.waitingForSlotSince),
    productsSelected: count(raw.productsSelected),
    productsPlanned: count(raw.productsPlanned),
    productsSkipped: count(raw.productsSkipped),
    productsSent: count(raw.productsSent),
    productsFailed: count(raw.productsFailed),
    productsNotStarted: count(raw.productsNotStarted),
    selectedByReason: toCountMap(raw.selectedByReason),
    skipped: list(raw.skipped)
      .map(toCoverageRedeliverSkip)
      .filter((skip): skip is CoverageRedeliverSkip => skip !== null),
    runsTotal: count(raw.runsTotal),
    runsDone: count(raw.runsDone),
    runsFailed: count(raw.runsFailed),
    runs: list(raw.runs).map(toCoverageRedeliverRun),
  };
}

/**
 * Start the coverage redeliver job of the connection's latest finished coverage run, or join the
 * one that exists (202 either way; `alreadyExisted` says which). No body: the server resolves the
 * products, their enrichment runs and their ids itself.
 */
export async function startCoverageRedeliver(
  connectionId: string,
): Promise<CoverageRedeliverStart> {
  const res = await integrationsClient
    .post(endpoints.connections.coverageRedeliver(connectionId))
    .json<ApiResponse<unknown>>();
  const data = isRecord(res.data) ? res.data : {};
  return {
    jobId: asString(data.jobId) ?? '',
    connectionId: asString(data.connectionId) ?? connectionId,
    snapshotDate: asString(data.snapshotDate) ?? '',
    alreadyExisted: data.alreadyExisted === true,
    state: asString(data.state) ?? '',
  };
}

/**
 * Status of a coverage redeliver job — the one named by `jobId`, or (without it) the job of the
 * connection's latest finished coverage run. Rejects with the 404 when there is no such job.
 */
export async function getCoverageRedeliverJob(
  connectionId: string,
  jobId?: string,
): Promise<CoverageRedeliverJob> {
  const res = await integrationsClient
    .get(
      jobId
        ? endpoints.connections.coverageRedeliverJob(connectionId, jobId)
        : endpoints.connections.coverageRedeliver(connectionId),
    )
    .json<ApiResponse<unknown>>();
  return toCoverageRedeliverJob(isRecord(res.data) ? res.data : {});
}

/** Project an audit-trail record onto the few fields the table needs; keep `raw`. */
function toConnectionEvent(raw: JsonMap): ConnectionEvent {
  const actor = pick(raw, ['actor', 'createdBy', 'userId', 'actorId']);
  const actorLabel = isRecord(actor)
    ? (asString(pick(actor, ['name', 'email', 'id'])) ?? null)
    : asString(actor);
  return {
    id: asString(pick(raw, ['id', 'eventId'])),
    eventType: asString(pick(raw, ['eventType', 'type', 'event', 'action'])),
    message: asString(pick(raw, ['message', 'description', 'summary'])),
    createdAt: asString(pick(raw, ['createdAt', 'timestamp', 'occurredAt', 'created_at'])),
    actor: actorLabel,
    raw,
  };
}

/** Audit trail for one connection (`POST /connections/{cid}/events/list`). */
export async function listConnectionEvents(
  connectionId: string,
  filters: ConnectionEventsFilters = {},
  page = 1,
  size = 20,
): Promise<ConnectionEventsPage> {
  const body = {
    filters: {
      ...(filters.eventType ? { eventType: filters.eventType } : {}),
      ...(filters.startDate ? { startDate: filters.startDate } : {}),
      ...(filters.endDate ? { endDate: filters.endDate } : {}),
    },
    pagination: { page, size },
  };
  const res = await integrationsClient
    .post(endpoints.connections.eventsList(connectionId), { json: body })
    .json<unknown>();
  const { items, pagination } = unwrapList(res, ['events', 'items', 'results']);
  return { items: items.map(toConnectionEvent), pagination };
}
